import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userHasGroup, userIsRealAppAdmin } from "@/lib/auth";
import {
  createMaintenanceAsset,
  createSubLocation,
  deleteMaintenanceAsset,
  getAssetByExternalId,
  getContractProjects,
  getHeroLocations,
  getMaintenanceAsset,
  getPublishedFormConfig,
  getSubLocations,
  listMaintenanceTypes,
  startMaintenanceEvent,
  updateMaintenanceAsset,
  type HeroLocation,
  type MaintenanceType,
  type SubLocation,
} from "@/lib/wartungen";
import { buildAssetEditPrefill } from "./assetEditPrefill";
import { RuntimeField } from "./form/RuntimeField";
import { isValueEmpty, normalizeFields, type NormalizedField } from "./form/runtimeModel";

type Tone = "info" | "success" | "error" | "warning";

function numParam(params: URLSearchParams, key: string): number | null {
  const n = Number(params.get(key));
  return Number.isFinite(n) && Number.isInteger(n) && n > 0 ? n : null;
}

/** Hängt `asset_id` an eine relative Rücksprung-URL an (damit die Zielseite das Objekt vorwählt). */
function withAssetParam(url: string, assetId: number): string {
  const [path, query = ""] = url.split("?");
  const usp = new URLSearchParams(query);
  usp.set("asset_id", String(assetId));
  return `${path}?${usp.toString()}`;
}

const EXTERNAL_ID_KEYS = ["external_id", "externalId", "kennung", "asset_external_id", "id_extern"];

function deriveExternalId(masterdata: Record<string, unknown>, fallback: string): string {
  for (const key of EXTERNAL_ID_KEYS) {
    const v = String(masterdata[key] ?? "").trim();
    if (v) return v;
  }
  return fallback;
}

export function ObjektAnlegenPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const canDeleteAsset = userIsRealAppAdmin(user) || userHasGroup(user, "App-Admins");

  const mode = (params.get("mode") || "").trim().toLowerCase() === "edit" ? "edit" : "create";
  const editAssetId = numParam(params, "asset_id");
  const isEdit = mode === "edit" && editAssetId != null;
  const contractId = numParam(params, "contract_id");
  const queryHeroAddressId = numParam(params, "hero_address_id");
  const querySubLocationId = numParam(params, "sub_location_id");
  const maintenanceEventId = numParam(params, "maintenance_event_id");
  const afterCreate = (params.get("after_create") || "").trim().toLowerCase();
  const returnTo = (params.get("return_to") || "").trim().toLowerCase();
  // Vollständige Rücksprung-URL (nur relative Pfade innerhalb der App erlaubt).
  const returnUrl = (() => {
    const raw = (params.get("return_url") || "").trim();
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "";
  })();
  const prefilledExternalId = (params.get("external_id") || "").trim();

  const [typeKey, setTypeKey] = useState((params.get("type_key") || params.get("type") || "").trim());
  const [availableTypes, setAvailableTypes] = useState<MaintenanceType[]>([]);

  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ tone: Tone; messages: string[] } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [masterdataFields, setMasterdataFields] = useState<NormalizedField[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [externalId, setExternalId] = useState(prefilledExternalId);
  const [originalExternalId, setOriginalExternalId] = useState("");
  const [externalIdLocked, setExternalIdLocked] = useState(isEdit);
  const [existingAsset, setExistingAsset] = useState<{ asset_id?: number; external_id?: string } | null>(null);

  const [locations, setLocations] = useState<HeroLocation[]>([]);
  const [subLocations, setSubLocations] = useState<SubLocation[]>([]);
  const [heroAddressId, setHeroAddressId] = useState<number | null>(queryHeroAddressId);
  const [subLocationId, setSubLocationId] = useState<number | null>(querySubLocationId);

  const loadSchema = useCallback(async (resolvedTypeKey: string) => {
    const schema = await getPublishedFormConfig({ typeKey: resolvedTypeKey });
    const fields = normalizeFields(schema).filter((f) => f.effective_scope === "masterdata");
    setMasterdataFields(fields);
    return fields;
  }, []);

  const loadSubLocs = useCallback(async (addrId: number) => {
    try {
      const subs = await getSubLocations(addrId);
      setSubLocations(subs);
    } catch {
      setSubLocations([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setFatalError(null);
      try {
        let resolvedTypeKey = typeKey;

        // Typ aus Vertrag ableiten, falls nicht gesetzt.
        if (!resolvedTypeKey && contractId) {
          const result = await getContractProjects(contractId, { limit: 1 });
          const types = Array.isArray(result.contract?.maintenance_types)
            ? (result.contract!.maintenance_types as MaintenanceType[])
            : [];
          setAvailableTypes(types);
          if (types.length === 1) resolvedTypeKey = String(types[0]?.key || "").trim();
        } else if (!resolvedTypeKey) {
          const all = await listMaintenanceTypes();
          setAvailableTypes(all);
        }

        if (mode === "edit" && editAssetId == null) {
          setFatalError("Für den Edit-Modus wird eine gültige asset_id benötigt.");
          return;
        }

        let loadedHeroAddressId: number | null = null;
        let loadedSubLocationId: number | null = null;

        if (resolvedTypeKey) {
          if (!cancelled) setTypeKey(resolvedTypeKey);
          const fields = await loadSchema(resolvedTypeKey);
          const emptyValues = () => {
            const initial: Record<string, unknown> = {};
            for (const f of fields) initial[f.field_key] = f.value ?? null;
            return initial;
          };

          if (isEdit && editAssetId != null) {
            try {
              const asset = await getMaintenanceAsset(resolvedTypeKey, editAssetId);
              const prefill = buildAssetEditPrefill(asset, fields, {
                heroAddressId: queryHeroAddressId,
                subLocationId: querySubLocationId,
                externalId: prefilledExternalId,
              });
              loadedHeroAddressId = prefill.heroAddressId;
              loadedSubLocationId = prefill.subLocationId;
              if (!cancelled) {
                setValues(prefill.values);
                if (prefill.externalId) {
                  setExternalId(prefill.externalId);
                  setOriginalExternalId(prefill.externalId);
                }
                if (prefill.heroAddressId) setHeroAddressId(prefill.heroAddressId);
                if (prefill.subLocationId) setSubLocationId(prefill.subLocationId);
              }
            } catch (err) {
              if (!cancelled) {
                setValues(emptyValues());
                setFeedback({
                  tone: "error",
                  messages: [
                    err instanceof ApiError
                      ? err.message
                      : "Die Stammdaten dieses Objekts konnten nicht geladen werden.",
                  ],
                });
              }
            }
          } else if (!cancelled) {
            setValues(emptyValues());
          }
        }

        const addressForLocations = loadedHeroAddressId ?? queryHeroAddressId;

        // Orte laden – auch wenn die Adresse nur am Objekt steht und nicht in der URL.
        try {
          if (addressForLocations) {
            const locs = await getHeroLocations({ hero_address_id: addressForLocations });
            if (!cancelled) {
              setLocations(locs);
              setHeroAddressId(addressForLocations);
            }
            await loadSubLocs(addressForLocations);
            if (!cancelled && loadedSubLocationId) setSubLocationId(loadedSubLocationId);
          } else {
            const locs = await getHeroLocations({ limit: 50 });
            if (!cancelled) setLocations(locs);
          }
        } catch {
          if (!cancelled) setLocations([]);
        }
      } catch (err) {
        if (!cancelled) {
          setFatalError(err instanceof ApiError ? err.message : "Seite konnte nicht initialisiert werden.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heroAddressLocked = queryHeroAddressId != null;

  async function checkExternalId() {
    const value = externalId.trim();
    if (!value || !typeKey) {
      setFeedback({ tone: "warning", messages: ["Bitte zuerst eine Kennung eingeben."] });
      return;
    }
    if (isEdit && value === originalExternalId.trim()) {
      setExistingAsset(null);
      setExternalIdLocked(true);
      setFeedback({ tone: "success", messages: ["Kennung ist unverändert."] });
      return;
    }
    try {
      const existing = await getAssetByExternalId(typeKey, value, heroAddressId);
      const existingId = Number(existing.asset?.asset_id);
      const belongsToThisAsset = isEdit && editAssetId != null && existingId === editAssetId;
      if (existing.exists && existingId > 0 && !belongsToThisAsset) {
        const currentLabel = String(existing.asset?.external_id || value);
        setExistingAsset({
          asset_id: existingId,
          external_id: currentLabel,
        });
        setExternalIdLocked(false);
        setFeedback({
          tone: "warning",
          messages: [
            existing.matched_previous_external_id
              ? `Die Kennung „${value}“ gehört bereits zum Objekt „${currentLabel}“.`
              : `Es existiert bereits ein Objekt mit der Kennung „${value}“.`,
          ],
        });
      } else {
        setExistingAsset(null);
        setExternalIdLocked(true);
        setValues((prev) => ({ ...prev, external_id: value }));
        setFeedback({
          tone: "success",
          messages: [
            isEdit
              ? "Neue Kennung ist frei. Das Objekt bleibt dasselbe, auch für offline erfasste Wartungen."
              : "Kennung ist frei. Bitte Stammdaten ausfüllen.",
          ],
        });
      }
    } catch (err) {
      setFeedback({
        tone: "error",
        messages: [err instanceof ApiError ? err.message : "Kennung konnte nicht geprüft werden."],
      });
    }
  }

  function setFieldValue(field: NormalizedField, value: unknown) {
    setValues((prev) => ({ ...prev, [field.field_key]: value }));
    setFieldErrors((prev) => {
      if (!prev[field.field_key]) return prev;
      const next = { ...prev };
      delete next[field.field_key];
      return next;
    });
  }

  async function handleCreateSubLocation() {
    if (!heroAddressId) {
      setFeedback({ tone: "warning", messages: ["Bitte zuerst einen Ort auswählen."] });
      return;
    }
    const name = window.prompt("Name für den neuen Unterort:");
    if (!name || !name.trim()) return;
    try {
      const created = await createSubLocation(heroAddressId, { name: name.trim() });
      await loadSubLocs(heroAddressId);
      if (created?.id) setSubLocationId(Number(created.id));
    } catch (err) {
      setFeedback({
        tone: "error",
        messages: [err instanceof ApiError ? err.message : "Unterort konnte nicht angelegt werden."],
      });
    }
  }

  function validate(): string[] {
    const errors: string[] = [];
    const map: Record<string, string> = {};
    for (const field of masterdataFields) {
      if (!field.resolved_required) continue;
      if (isValueEmpty(values[field.field_key])) {
        errors.push(`${field.effective_label}: Pflichtfeld fehlt.`);
        map[field.field_key] = "Pflichtfeld fehlt.";
      }
    }
    setFieldErrors(map);
    return errors;
  }

  async function continueFlow(assetId: number, savedExternalId: string) {
    if (isEdit) {
      let eventId = maintenanceEventId;
      if (!eventId && returnTo === "form") {
        const result = await startMaintenanceEvent(typeKey, { asset_id: assetId });
        eventId = Number(result.maintenance_event_id) || null;
      }
      if (eventId) {
        navigate(`/wartungen/form?type_key=${encodeURIComponent(typeKey)}&asset_id=${assetId}&maintenance_event_id=${eventId}`);
        return;
      }
      if (returnUrl) {
        navigate(withAssetParam(returnUrl, assetId));
        return;
      }
      navigate(`/wartungen/objekt-uebersicht`);
      return;
    }

    if (["selection", "select", "lookup"].includes(afterCreate)) {
      const usp = new URLSearchParams();
      usp.set("type", typeKey);
      usp.set("external_id", savedExternalId);
      usp.set("asset_id", String(assetId));
      if (contractId) usp.set("contract_id", String(contractId));
      if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
      if (subLocationId) usp.set("sub_location_id", String(subLocationId));
      navigate(`/wartungen/objekt-auswahl?${usp.toString()}`);
      return;
    }

    if (["huelle_detail", "huelle-detail", "huelle"].includes(afterCreate)) {
      if (returnUrl) {
        navigate(withAssetParam(returnUrl, assetId));
        return;
      }
      const usp = new URLSearchParams({ asset_id: String(assetId), type_key: typeKey });
      if (contractId) usp.set("contract_id", String(contractId));
      if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
      if (subLocationId) usp.set("sub_location_id", String(subLocationId));
      navigate(`/wartungen/huelle-detail?${usp.toString()}`);
      return;
    }

    const result = await startMaintenanceEvent(typeKey, { asset_id: assetId });
    const eventId = Number(result.maintenance_event_id);
    navigate(`/wartungen/form?type_key=${encodeURIComponent(typeKey)}&asset_id=${assetId}&maintenance_event_id=${eventId}`);
  }

  async function handleSave() {
    const kennungChanged = isEdit && externalId.trim() !== originalExternalId.trim();
    if ((!isEdit && !externalIdLocked) || (kennungChanged && !externalIdLocked)) {
      setFeedback({
        tone: "warning",
        messages: ["Bitte Kennung zuerst erfolgreich prüfen, bevor das Objekt gespeichert wird."],
      });
      return;
    }
    const errors = validate();
    if (errors.length) {
      setFeedback({ tone: "error", messages: errors });
      return;
    }
    if (kennungChanged) {
      const confirmed = window.confirm(
        `Kennung von „${originalExternalId.trim() || "ohne Kennung"}“ in „${externalId.trim()}“ ändern?\n\nDas Objekt bleibt dasselbe. Wartungen, die ein Monteur offline daran erfasst hat oder noch erfasst, werden beim Synchronisieren hier zusammengeführt.`,
      );
      if (!confirmed) return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const masterdata: Record<string, unknown> = { ...values };
      if (heroAddressId) masterdata.hero_address_id = heroAddressId;
      if (subLocationId) masterdata.sub_location_id = subLocationId;
      const resolvedExternalId = isEdit
        ? externalId.trim()
        : deriveExternalId(masterdata, externalId.trim());
      if (!resolvedExternalId) throw new Error("external_id konnte nicht aus den Stammdaten ermittelt werden.");
      masterdata.external_id = resolvedExternalId;

      const payload = {
        external_id: resolvedExternalId,
        name: String(masterdata.name ?? masterdata.bezeichnung ?? resolvedExternalId),
        status: "active",
        ...(kennungChanged ? { rename_external_id: true } : {}),
        ...(contractId ? { contract_id: contractId } : {}),
        masterdata,
      };

      const result = isEdit
        ? await updateMaintenanceAsset(typeKey, editAssetId!, payload)
        : await createMaintenanceAsset(typeKey, payload);
      const created = result.asset ?? result;
      const assetId = Number(created?.asset_id ?? editAssetId);
      if (!Number.isFinite(assetId) || assetId <= 0) throw new Error("Ungültige asset_id nach dem Speichern.");

      setFeedback({
        tone: "success",
        messages: [isEdit ? "Objekt wurde aktualisiert. Weiterleitung …" : "Objekt wurde angelegt. Weiterleitung …"],
      });
      await continueFlow(assetId, resolvedExternalId);
    } catch (err) {
      setFeedback({
        tone: "error",
        messages: [err instanceof ApiError ? err.message : "Objekt konnte nicht gespeichert werden."],
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit || !editAssetId || !typeKey || !canDeleteAsset) return;
    const label = externalId.trim() || `#${editAssetId}`;
    if (
      !window.confirm(
        `Objekt „${label}“ wirklich löschen?\n\nDas Objekt verschwindet aus Web- und iOS-App. Abgeschlossene Wartungshistorie bleibt erhalten.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setFeedback(null);
    try {
      await deleteMaintenanceAsset(typeKey, editAssetId);
      setFeedback({ tone: "success", messages: ["Objekt wurde gelöscht. Weiterleitung …"] });
      if (returnUrl) {
        navigate(returnUrl);
        return;
      }
      if (["huelle_detail", "huelle-detail", "huelle"].includes(returnTo) || contractId) {
        const usp = new URLSearchParams();
        if (typeKey) usp.set("type_key", typeKey);
        if (contractId) usp.set("contract_id", String(contractId));
        if (heroAddressId) usp.set("hero_address_id", String(heroAddressId));
        navigate(`/wartungen/huelle-detail?${usp.toString()}`);
        return;
      }
      navigate("/wartungen/objekt-uebersicht");
    } catch (err) {
      setFeedback({
        tone: "error",
        messages: [err instanceof ApiError ? err.message : "Objekt konnte nicht gelöscht werden."],
      });
    } finally {
      setDeleting(false);
    }
  }

  const showCategorySelect = !typeKey && availableTypes.length > 1;
  const identifierStepActive = !isEdit && !externalIdLocked;

  const sortedSubLocations = useMemo(
    () =>
      subLocations.map((s) => ({
        id: Number(s.id),
        label: String(s.path_label || (Array.isArray(s.path_segments) ? s.path_segments.join(" > ") : "") || s.name || `Unterort ${s.id}`),
      })),
    [subLocations],
  );

  const externalInputRef = useRef<HTMLInputElement | null>(null);

  if (fatalError) {
    return (
      <AppShell title="Objekt anlegen" back={{ to: "/wartungen", label: "zurück zur Übersicht" }}>
        <Alert variant="error">{fatalError}</Alert>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={isEdit ? "Objekt bearbeiten" : "Objekt anlegen"}
      subtitle={typeKey ? `Kategorie: ${typeKey}` : undefined}
      back={
        returnUrl
          ? { to: returnUrl, label: "zurück zur Wartung" }
          : {
              to: typeKey ? `/wartungen/kategorie?kategorie=${encodeURIComponent(typeKey)}` : "/wartungen",
              label: "zurück",
            }
      }
    >
      {feedback ? (
        <Alert
          variant={feedback.tone === "success" ? "success" : feedback.tone === "warning" ? "warning" : feedback.tone === "error" ? "error" : "info"}
          className="mb-4"
        >
          {feedback.messages.length === 1 ? (
            feedback.messages[0]
          ) : (
            <ul className="list-inside list-disc">
              {feedback.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </Alert>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade …
        </p>
      ) : (
        <div className="space-y-6">
          {showCategorySelect ? (
            <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
              <label className="space-y-1">
                <span className="eh-label text-xs">Wartungskategorie</span>
                <select
                  className="eh-input w-full max-w-md"
                  value={typeKey}
                  onChange={(e) => {
                    setTypeKey(e.target.value);
                    if (e.target.value) void loadSchema(e.target.value);
                  }}
                >
                  <option value="">Bitte Kategorie auswählen …</option>
                  {availableTypes.map((t) => (
                    <option key={String(t.key)} value={String(t.key)}>
                      {t.name || t.key}
                    </option>
                  ))}
                </select>
              </label>
            </section>
          ) : null}

          <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
            <h3 className="mb-3 text-base font-semibold text-label">Kennung</h3>
            <div className="flex flex-wrap items-end gap-2">
              <label className="space-y-1">
                <span className="eh-label text-xs">Kennung</span>
                <input
                  ref={externalInputRef}
                  className="eh-input w-64"
                  value={externalId}
                  disabled={externalIdLocked}
                  onChange={(e) => {
                    setExternalId(e.target.value);
                    setExistingAsset(null);
                  }}
                />
              </label>
              {externalIdLocked ? (
                <Button type="button" variant="secondary" onClick={() => setExternalIdLocked(false)}>
                  Kennung ändern
                </Button>
              ) : (
                <Button type="button" variant="secondary" onClick={() => void checkExternalId()}>
                  Kennung prüfen
                </Button>
              )}
            </div>
            {isEdit ? (
              <p className="mt-2 text-sm text-label-secondary">
                Tippfehler lassen sich hier korrigieren. Das Objekt und seine Wartungen bleiben erhalten, auch wenn ein Monteur offline noch mit der alten Kennung arbeitet.
              </p>
            ) : null}
            {existingAsset?.asset_id ? (
              <p className="mt-2 text-sm text-label-secondary">
                Bestehendes Objekt:{" "}
                <a
                  href={`/wartungen/huelle-detail?asset_id=${existingAsset.asset_id}&type_key=${encodeURIComponent(typeKey)}`}
                  className="text-accent hover:underline"
                >
                  {existingAsset.external_id} öffnen
                </a>
              </p>
            ) : null}
          </section>

          {(isEdit || externalIdLocked) && typeKey ? (
            <>
              <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
                <h3 className="mb-3 text-base font-semibold text-label">Standort</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="eh-label text-xs">Ort</span>
                    <select
                      className="eh-input w-full"
                      disabled={heroAddressLocked}
                      value={heroAddressId ? String(heroAddressId) : ""}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        const next = Number.isFinite(n) && n > 0 ? n : null;
                        setHeroAddressId(next);
                        setSubLocationId(null);
                        if (next) void loadSubLocs(next);
                        else setSubLocations([]);
                      }}
                    >
                      <option value="">Bitte Ort auswählen …</option>
                      {locations.map((loc) => {
                        const id = Number(loc.hero_address_id ?? loc.id);
                        return (
                          <option key={id} value={id}>
                            {String(loc.label || loc.name || `Ort ${id}`)}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <div className="space-y-1">
                    <span className="eh-label text-xs">Unterort</span>
                    <div className="flex gap-2">
                      <select
                        className="eh-input w-full"
                        value={subLocationId ? String(subLocationId) : ""}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setSubLocationId(Number.isFinite(n) && n > 0 ? n : null);
                        }}
                      >
                        <option value="">Bitte Unterort auswählen …</option>
                        {sortedSubLocations.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <Button type="button" variant="secondary" onClick={handleCreateSubLocation}>
                        +
                      </Button>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
                <h3 className="mb-3 text-base font-semibold text-label">Stammdaten</h3>
                {masterdataFields.length === 0 ? (
                  <p className="text-sm text-label-secondary">Für diese Kategorie sind keine Stammdatenfelder konfiguriert.</p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {masterdataFields
                      .filter((f) => f.resolved_visible)
                      .map((field) => (
                        <RuntimeField
                          key={field.field_key}
                          field={field}
                          value={values[field.field_key]}
                          error={fieldErrors[field.field_key]}
                          onChange={(v) => setFieldValue(field, v)}
                        />
                      ))}
                  </div>
                )}
              </section>

              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" disabled={saving || deleting} onClick={handleSave}>
                  {saving ? "Speichere …" : isEdit ? "Objekt aktualisieren" : "Objekt anlegen"}
                </Button>
                {isEdit && canDeleteAsset ? (
                  <Button
                    type="button"
                    variant="danger"
                    disabled={saving || deleting}
                    onClick={() => void handleDelete()}
                  >
                    {deleting ? "Lösche …" : "Objekt löschen"}
                  </Button>
                ) : null}
              </div>
            </>
          ) : identifierStepActive ? (
            <p className="text-sm text-label-secondary">
              Bitte zuerst eine Kennung prüfen, um die Stammdaten auszufüllen.
            </p>
          ) : null}
        </div>
      )}
    </AppShell>
  );
}
